import { useEffect, useState } from 'react'
import { CurrencyField } from '@/components/currency-field'
import { formatPhone } from '@/lib/phone'
import { useDispatch } from 'react-redux'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { AlertCircleIcon, ChevronDownIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Checkbox,
	cn,
	ConfirmDialog,
	Form, FormControl, FormMessage, FormDescription, FormDialog, FormField, FormItem, FormLabel,
	Input,
	PhoneInput,
	Popover, PopoverContent, PopoverTrigger,
	RadioGroup, RadioGroupItem,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import {
	contactsApi,
	dealsApi,
	propertiesApi,
	useGetContactQuery,
	useListContactsQuery,
	useListPropertiesQuery,
	useStartRentalMutation,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { formatPropertyAddress } from '@/lib/property-address'
import type { CurrencyCode, StartRentalBody } from '@/types'
import { CURRENCIES, CURRENCY_SIGNS, RENTAL_PERIODS } from '@/types'
import { useAccountCurrency } from '@/lib/use-account-currency'
import {
	makeDefaultValues,
	startRentalSchema,
	type StartRentalFormValues,
	type StartRentalPreset,
} from './schema'

interface StartRentalDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	preset?: StartRentalPreset
}

function normalizeAmount(value: string): string {
	return value.replace(/\s/g, '').replace(',', '.')
}

interface StartRentalSuccess {
	rentalId: string
	warnings: string[]
}

// Body of the gateway's 409 when the new tenant's phone already belongs to a
// live contact of the account (`StartRentalService.resolveContact`).
interface ContactPhoneConflict {
	existingContactId: string
	existingContactName: string
	phone?: string
}

function extractContactConflict(err: unknown): ContactPhoneConflict | null {
	const e = err as { status?: number; data?: Record<string, unknown> } | null
	const data = e?.data
	if (e?.status !== 409 || !data || data.code !== 'CONTACT_PHONE_CONFLICT') return null
	if (typeof data.existingContactId !== 'string') return null
	return {
		existingContactId:   data.existingContactId,
		existingContactName: typeof data.existingContactName === 'string' ? data.existingContactName : '',
		phone:               typeof data.phone === 'string' ? data.phone : undefined,
	}
}

export function StartRentalDialog({ open, onOpenChange, preset }: StartRentalDialogProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const accountCurrency = useAccountCurrency()

	const [submitError,   setSubmitError]   = useState<string | null>(null)
	const [contactSearch, setContactSearch] = useState('')
	const [contactOpen,   setContactOpen]   = useState(false)
	// Set after a successful submit that returned warnings — drives the
	// «Аренда создана» info dialog shown before navigating to the rental.
	const [success, setSuccess] = useState<StartRentalSuccess | null>(null)
	// Set when the server refused a new contact because the phone is taken —
	// drives the «использовать существующего» confirm.
	const [conflict, setConflict] = useState<ContactPhoneConflict | null>(null)

	const [startRental, { isLoading }] = useStartRentalMutation()

	// `currentData`/`isFetching` (not `data`/`isLoading`): every keystroke
	// changes the search arg, so `data` would keep the previous page's results
	// and `isLoading` only covers the first-ever request.
	const { currentData: contactsData, isFetching: isFetchingContacts } = useListContactsQuery(
		{ type: 'TENANT', search: contactSearch.trim() || undefined, limit: 50 },
		{ skip: !open },
	)
	const { data: propertiesData, isLoading: isLoadingProperties } = useListPropertiesQuery({ limit: 200 }, { skip: !open })

	const form = useForm<StartRentalFormValues>({
		resolver:      zodResolverI18n(startRentalSchema),
		defaultValues: makeDefaultValues(preset),
		mode:          'onBlur',
	})

	// Re-seed from the preset on every open so a dialog reused across
	// entry points (list header, kanban, tenant page) starts fresh.
	useEffect(() => {
		if (!open) return
		form.reset(makeDefaultValues(preset))
		setSubmitError(null)
		setConflict(null)
		setContactSearch('')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	const contacts   = contactsData?.items ?? []
	const properties = propertiesData?.properties ?? []

	const tenantMode = form.watch('tenantMode')
	const contactId  = form.watch('contactId')
	const propertyId = form.watch('propertyId')

	// The rental is denominated in the property's own currency — the amounts
	// prefilled below are sums in that currency. Only a property without rent
	// terms falls back to the account default; nothing here is hardcoded to
	// RUB any more.
	const selectedProperty = properties.find((p) => p.id === propertyId)
	const storedCurrency = selectedProperty?.rentTerms?.rentCurrency
	const rentalCurrency: CurrencyCode =
		storedCurrency && (CURRENCIES as readonly string[]).includes(storedCurrency)
			? (storedCurrency as CurrencyCode)
			: accountCurrency

	// The picked contact may be missing from the searched page — fetch it
	// directly so the trigger can always render its name (preset case).
	const { data: pickedContact } = useGetContactQuery(contactId ?? '', {
		skip: !open || !contactId,
	})
	const selectedContact =
		contacts.find((c) => c.id === contactId) ?? (pickedContact?.id === contactId ? pickedContact : undefined)

	// Prefill terms from the selected property's rent terms, unless the user
	// (or the kanban deal preset) already provided an amount. Kept editable.
	useEffect(() => {
		if (!open || !propertyId) return
		const property = properties.find((p) => p.id === propertyId)
		if (!property) return
		const rentAmount    = property.rentTerms?.rentAmount    ?? property.rentAmount
		const depositAmount = property.rentTerms?.depositAmount ?? property.depositAmount
		if (!form.getValues('rentAmount') && rentAmount && Number(rentAmount) > 0) {
			form.setValue('rentAmount', rentAmount)
		}
		if (!form.getValues('depositAmount') && depositAmount && Number(depositAmount) > 0) {
			form.setValue('depositAmount', depositAmount)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, propertyId, properties])

	async function onSubmit(values: StartRentalFormValues) {
		setSubmitError(null)
		const contact: StartRentalBody['contact'] =
			values.tenantMode === 'existing'
				? { id: values.contactId ?? '' }
				: {
						name:  (values.name ?? '').trim(),
						phone: values.phone?.trim() || undefined,
						email: values.email?.trim() || undefined,
					}
		try {
			const result = await startRental({
				contact,
				propertyId: values.propertyId,
				dealId:     preset?.dealId,
				terms: {
					rentAmount:    normalizeAmount(values.rentAmount),
					currency:      rentalCurrency,
					period:        values.period,
					depositAmount: values.depositAmount?.trim()
						? normalizeAmount(values.depositAmount)
						: undefined,
				},
				startDate:       values.startDate,
				endDate:         values.endDate || undefined,
				enableAppAccess: values.enableAppAccess,
			}).unwrap()

			// The orchestrator also touches contacts, properties and deals —
			// those caches live in separate RTK Query slices, so invalidate
			// them here (the mutation itself only invalidates rentals).
			dispatch(contactsApi.util.invalidateTags([{ type: 'Contact', id: 'LIST' }]))
			dispatch(propertiesApi.util.invalidateTags([
				{ type: 'Property', id: values.propertyId },
				{ type: 'Property', id: 'LIST' },
			]))
			if (preset?.dealId) {
				dispatch(dealsApi.util.invalidateTags([
					{ type: 'Deal', id: preset.dealId },
					{ type: 'Deal', id: 'LIST' },
				]))
			}

			form.reset(makeDefaultValues())
			onOpenChange(false)

			if (result.warnings.length > 0) {
				setSuccess({ rentalId: result.rental.id, warnings: result.warnings })
			} else {
				void navigate({ to: '/rent/$rentalId', params: { rentalId: result.rental.id } })
			}
		} catch (err) {
			const phoneConflict = extractContactConflict(err)
			if (phoneConflict) {
				setConflict(phoneConflict)
				return
			}
			setSubmitError(extractErrorMessage(err) ?? t('rent.startDialog.errors.generic'))
		}
	}

	// Switch the form to the existing tenant the server pointed at and submit
	// again — everything else the user typed (terms, dates) is kept.
	async function useExistingContact() {
		if (!conflict) return
		form.setValue('tenantMode', 'existing')
		form.setValue('contactId', conflict.existingContactId)
		setConflict(null)
		await form.handleSubmit(onSubmit)()
	}

	function goToRental() {
		if (!success) return
		const { rentalId } = success
		setSuccess(null)
		void navigate({ to: '/rent/$rentalId', params: { rentalId } })
	}

	return (
		<>
			<Form {...form}>
				<FormDialog
					open={open}
					onOpenChange={onOpenChange}
					title={t('rent.startDialog.title')}
					onSubmit={form.handleSubmit(onSubmit)}
					submitLabel={isLoading ? t('common.saving') : t('rent.startDialog.submit')}
					cancelLabel={t('common.cancel')}
					closeAriaLabel={t('common.close')}
					isSubmitting={isLoading}
					contentClassName="sm:max-w-[34rem]"
				>
					{/* ── Арендатор ─────────────────────────────── */}
					<section className="flex flex-col gap-3">
						<h3 className="text-sm font-[500] text-(--color-muted-foreground)">
							{t('rent.startDialog.tenantSection')}
						</h3>

						<FormField
							control={form.control}
							name="tenantMode"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={(v) => {
												field.onChange(v)
												form.clearErrors(['contactId', 'name', 'email'])
											}}
											className="flex items-center gap-2"
										>
											{(['existing', 'new'] as const).map((mode) => (
												<label
													key={mode}
													className={cn(
														'flex cursor-pointer select-none items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition-colors',
														field.value === mode
															? 'border-(--color-primary) bg-(--color-primary)/5 text-(--color-foreground)'
															: 'border-(--color-border) text-(--color-muted-foreground) hover:bg-(--color-muted)/60',
													)}
												>
													<RadioGroupItem value={mode} className="sr-only" />
													{t(`rent.startDialog.tenant${mode === 'existing' ? 'Existing' : 'New'}`)}
												</label>
											))}
										</RadioGroup>
									</FormControl>
								</FormItem>
							)}
						/>

						{tenantMode === 'existing' ? (
							<FormField
								control={form.control}
								name="contactId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('rent.startDialog.contact')}</FormLabel>
										<Popover open={contactOpen} onOpenChange={setContactOpen}>
											<PopoverTrigger
												render={
													<button
														type="button"
														className="flex h-9 w-full items-center justify-between gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-card) px-3 text-sm"
													/>
												}
											>
												<span className={cn('truncate', !selectedContact && 'text-(--color-muted-foreground)')}>
													{selectedContact
														? [selectedContact.name, formatPhone(selectedContact.phone) || selectedContact.email]
															.filter(Boolean)
															.join(' · ')
														: t('rent.startDialog.contactPlaceholder')}
												</span>
												<ChevronDownIcon className="size-4 shrink-0 text-(--color-muted-foreground)" />
											</PopoverTrigger>
											<PopoverContent
												align="start"
												className="flex w-(--anchor-width) flex-col gap-1 rounded-(--radius-md) bg-(--color-popover) p-1 shadow-md ring-1 ring-foreground/10"
											>
												<SearchInput
													autoFocus
													placeholder={t('rent.startDialog.contactSearchPlaceholder')}
													value={contactSearch}
													onChange={(e) => setContactSearch(e.target.value)}
												/>
												<div className="max-h-60 overflow-auto">
													{isFetchingContacts && (
														<p className="px-3 py-2 text-sm text-(--color-muted-foreground)">
															{t('common.loading')}
														</p>
													)}
													{!isFetchingContacts && contacts.length === 0 && (
														<p className="px-3 py-2 text-sm text-(--color-muted-foreground)">
															{t('rent.startDialog.contactsEmpty')}
														</p>
													)}
													{contacts.map((c) => (
														<button
															key={c.id}
															type="button"
															onClick={() => {
																field.onChange(c.id)
																setContactOpen(false)
															}}
															className={cn(
																'flex w-full flex-col gap-0.5 rounded-(--radius-sm) px-3 py-2 text-left text-sm hover:bg-(--color-muted)',
																c.id === field.value && 'bg-(--color-muted)/60',
															)}
														>
															<span className="truncate font-[500] text-(--color-foreground)">{c.name}</span>
															{(c.phone || c.email) && (
																<span className="truncate text-xs text-(--color-muted-foreground)">
																	{[formatPhone(c.phone), c.email].filter(Boolean).join(' · ')}
																</span>
															)}
														</button>
													))}
												</div>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
						) : (
							<>
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('rent.startDialog.name')}</FormLabel>
											<FormControl>
												<Input placeholder={t('rent.startDialog.namePlaceholder')} {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<div className="grid grid-cols-2 gap-3">
									<FormField
										control={form.control}
										name="phone"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('rent.startDialog.phone')}</FormLabel>
												<FormControl>
													<PhoneInput value={field.value ?? ''} onChange={field.onChange} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('rent.startDialog.email')}</FormLabel>
												<FormControl>
													<Input type="email" {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</>
						)}
					</section>

					{/* ── Объект ────────────────────────────────── */}
					<section className="flex flex-col gap-3">
						<h3 className="text-sm font-[500] text-(--color-muted-foreground)">
							{t('rent.startDialog.propertySection')}
						</h3>
						<FormField
							control={form.control}
							name="propertyId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('rent.startDialog.property')}</FormLabel>
									<Select onValueChange={field.onChange} value={field.value} disabled={isLoadingProperties}>
										<FormControl>
											<SelectTrigger width="full">
												<SelectValue placeholder={isLoadingProperties ? t('common.loading') : t('rent.startDialog.propertyPlaceholder')} />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{properties.map((p) => {
												// A preseeded property stays selectable even if it is
												// not AVAILABLE (e.g. re-renting straight from a deal).
												const unavailable = p.status !== 'AVAILABLE' && p.id !== preset?.propertyId
												return (
													<SelectItem key={p.id} value={p.id} disabled={unavailable}>
														{formatPropertyAddress(p.city, p.address)}
														{unavailable ? ` — ${t('rent.startDialog.propertyUnavailable')}` : ''}
													</SelectItem>
												)
											})}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</section>

					{/* ── Условия ───────────────────────────────── */}
					<section className="flex flex-col gap-3">
						<h3 className="text-sm font-[500] text-(--color-muted-foreground)">
							{t('rent.startDialog.termsSection')}
						</h3>
						<div className="grid grid-cols-2 gap-3">
							<FormField
								control={form.control}
								name="rentAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('rent.startDialog.rentAmount')}, {CURRENCY_SIGNS[rentalCurrency] ?? rentalCurrency}
										</FormLabel>
										<FormControl>
											<Input inputMode="decimal" placeholder="50 000" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="depositAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('rent.startDialog.depositAmount')}, {CURRENCY_SIGNS[rentalCurrency] ?? rentalCurrency}
										</FormLabel>
										<FormControl>
											<Input inputMode="decimal" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<FormField
								control={form.control}
								name="period"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('rent.startDialog.period')}</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger width="full">
													<SelectValue />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{RENTAL_PERIODS.map((p) => (
													<SelectItem key={p} value={p}>
														{t(`rent.startDialog.periods.${p.toLowerCase()}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
							{/* Валюта записи — показывается, а не спрашивается: её задают
							    один раз в профиле. Общий компонент, а не своя копия: та
							    печатала «RUB (₽)» вместо переведённого названия. */}
							<CurrencyField label={t('rent.startDialog.currency')} value={rentalCurrency} />
						</div>
					</section>

					{/* ── Даты ──────────────────────────────────── */}
					<section className="flex flex-col gap-3">
						<h3 className="text-sm font-[500] text-(--color-muted-foreground)">
							{t('rent.startDialog.datesSection')}
						</h3>
						<div className="grid grid-cols-2 gap-3">
							<FormField
								control={form.control}
								name="startDate"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('rent.startDialog.startDate')}</FormLabel>
										<FormControl>
											<Input type="date" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="endDate"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('rent.startDialog.endDate')}</FormLabel>
										<FormControl>
											<Input type="date" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</section>

					<FormField
						control={form.control}
						name="enableAppAccess"
						render={({ field }) => (
							<FormItem>
								<div className="flex items-center gap-2">
									<FormControl>
										<Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />
									</FormControl>
									<FormLabel className="font-[500]">{t('rent.startDialog.enableAppAccess')}</FormLabel>
								</div>
								<FormDescription>{t('rent.startDialog.enableAppAccessHint')}</FormDescription>
							</FormItem>
						)}
					/>

					{submitError && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertTitle>{submitError}</AlertTitle>
						</Alert>
					)}
				</FormDialog>
			</Form>

			{/* Non-blocking follow-up warnings — shown before navigating.
			    «Закрыть» stays on the current page; «Перейти к аренде» navigates. */}
			<ConfirmDialog
				open={success !== null}
				onOpenChange={(v) => {
					if (!v) setSuccess(null)
				}}
				title={t('rent.startDialog.successTitle')}
				description={t('rent.startDialog.successWarningsDescription')}
				cancelLabel={t('common.close')}
				confirmLabel={t('rent.startDialog.goToRental')}
				onConfirm={goToRental}
			>
				{success && (
					<Alert variant="warning">
						<AlertCircleIcon className="size-4" />
						<AlertDescription>
							<ul className="list-disc pl-4">
								{success.warnings.map((code) => (
									<li key={code}>
										{t(`rent.startDialog.warnings.${code}`, { defaultValue: code })}
									</li>
								))}
							</ul>
						</AlertDescription>
					</Alert>
				)}
			</ConfirmDialog>

			{/* The phone is already taken by a live contact of this account —
			    offer to start the rental with that tenant instead of a new row. */}
			<ConfirmDialog
				open={conflict !== null}
				onOpenChange={(v) => {
					if (!v) setConflict(null)
				}}
				title={t('rent.startDialog.duplicateContact.title')}
				description={t('rent.startDialog.duplicateContact.description', {
					name:  conflict?.existingContactName,
					phone: formatPhone(conflict?.phone) || conflict?.phone,
				})}
				cancelLabel={t('rent.startDialog.duplicateContact.cancel')}
				confirmLabel={t('rent.startDialog.duplicateContact.useExisting')}
				onConfirm={useExistingContact}
				loading={isLoading}
			/>
		</>
	)
}
